import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NotificationService } from '@app/notification/services/notification.service';
import {
  MyProfileRoute,
  ProfileDetail,
} from '@app/profile/models/profile.models';
import { PersonalProfileService } from '@app/profile/services/personal-profile.service';
import {
  getDefaultFilterOption,
  getSearchFilterOptions,
} from '@app/search/constants/filter-options.constant';
import { HeaderProfileComponent } from '@app/shell/components/header-profile/header-profile.component';
import { LANGUAGE_LABELS } from '@app/shell/constants/language-labels.constant';
import { TenantLanguage } from '@app/tenant-language/models/tenant-language.model';
import { TenantLanguageService } from '@app/tenant-language/services/tenant-language.service';
import { AUTH_SERVICE } from '@auth/auth.token';
import { IAuthService } from '@auth/index';
import { BroadcasterService } from '@avenir-client-web/broadcaster';
import { LanguageService } from '@avenir-client-web/language';
import { APP_CONFIG } from '@avenir-client-web/models';
import {
  FilterOptionValue,
  SearchModule,
  SearchQueryOption,
} from '@avenir-client-web/search';
import { EventKeys } from '@avenir-client-web/signalr';
import { CredentialsService } from '@core/authentication/credentials.service';
import { AppRootRoute } from '@core/enums/app-route.enum';
import { User } from '@core/models/profile.model';
import { TenantRole } from '@core/models/role.model';
import { UserPermission } from '@core/services/permission.model';
import { PermissionService } from '@core/services/permission.service';
import { ProfileService } from '@core/services/profile.service';
import { AppContentService } from '@core/services/static/app-content.service';
import { GlobalNotificationService } from '@shared/services/global-notification.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { TieredMenuModule } from 'primeng/tieredmenu';
import {
  filter,
  filter as filterOperator,
  map,
  merge,
  mergeMap,
  Observable,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    RouterModule,
    AngularSvgIconModule,
    TieredMenuModule,
    SearchModule,
    HeaderProfileComponent,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('searchTool') searchElement: ElementRef;

  profile: User = this.profileService.profile;

  items: MenuItem[];

  roleItems: MenuItem[];

  searchFilterOptions = getSearchFilterOptions(this.config);

  hideTopBarSearch$: Observable<boolean>;

  avatarUrl$: Observable<SafeUrl>;

  isSearchActive = false;

  appRootRoute = AppRootRoute;

  filter: FilterOptionValue;

  hasUnread$: Observable<boolean>;

  featureToggle = this.config.featureToggle;

  supportedLanguages: TenantLanguage[];

  private readonly compUnsubscribe$ = new Subject<void>();

  constructor(
    @Inject(APP_CONFIG) readonly config: any,
    @Inject(AUTH_SERVICE) private readonly authService: IAuthService,
    private readonly credentialsService: CredentialsService,
    private readonly permissionService: PermissionService,
    private readonly router: Router,
    private readonly profileService: ProfileService,
    private readonly notificationService: NotificationService,
    private readonly broadcaster: BroadcasterService,
    private readonly globalNotificationService: GlobalNotificationService,
    private readonly languageService: LanguageService,
    private readonly tenantLanguageService: TenantLanguageService,
    private readonly personalProfileService: PersonalProfileService
  ) {
    this.setTopBarSearchState();
  }

  @HostListener('document:click', ['$event.target'])
  onClick(targetElement: ElementRef): void {
    const clickedInside =
      this.searchElement?.nativeElement?.contains(targetElement);

    this.isSearchActive = clickedInside;
  }

  ngOnInit(): void {
    this.setMenuItems();
    this.getAvatarUrl();
    this.checkUnread();
    this.handleNotificationToast();

    this.permissionService.roleDummyChanges$.subscribe(() => {
      this.setMenuItems();
    });
  }

  ngOnDestroy(): void {
    this.compUnsubscribe$.next();
    this.compUnsubscribe$.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  navigateToSearchResultsPage({ keyword }: SearchQueryOption): void {
    const content = AppContentService.getCurrentContent();

    this.filter = getDefaultFilterOption(
      this.searchFilterOptions,
      content.contentType,
      this.filter
    );

    this.router.navigate([AppRootRoute.SEARCH], {
      queryParams: {
        keyword,
        filter: this.filter,
      },
      state: {
        isShownFilterMsg: true,
      },
    });
  }

  onBellClick(): void {
    const shouldRefreshNotificationPage =
      this.router.url.split('/').pop() === AppRootRoute.NOTIFICATIONS;

    shouldRefreshNotificationPage &&
      this.globalNotificationService.isCurrentPage$.next();
  }

  private handleNotificationToast(): void {
    if (!this.featureToggle.tickets) return;
    this.authService.loginSuccess$
      .pipe(mergeMap(() => this.notificationService.checkUnread()))
      .pipe(filter(Boolean), takeUntil(this.compUnsubscribe$))
      .subscribe(() => this.globalNotificationService.showNotificationToast());
  }

  private checkUnread(): void {
    if (!this.featureToggle.tickets) return;
    this.hasUnread$ = merge(
      this.notificationService.checkUnread(),
      this.broadcaster
        .on(EventKeys.GLOBAL.BELL_INDICATOR)
        .pipe(map(count => !!count))
    ).pipe(takeUntil(this.compUnsubscribe$));
  }

  private getAvatarUrl(): void {
    this.avatarUrl$ = this.profileService
      .getAvatarUrl(this.profile.externalId)
      .pipe(takeUntil(this.compUnsubscribe$));
  }

  private setTopBarSearchState(): void {
    this.hideTopBarSearch$ = this.router.events.pipe(
      filterOperator(event => event instanceof NavigationEnd),
      map(event => event.url),
      startWith(this.router.url),
      map(url => url.includes(`/${AppRootRoute.SEARCH}`)),
      takeUntil(this.compUnsubscribe$)
    );
  }

  private logout(): void {
    this.authService.logout();
  }

  private switchRole(role: TenantRole): void {
    this.credentialsService.beforeChangeRole$.next(role);
    this.credentialsService.onRoleSwitched$.subscribe(() =>
      this.setMenuItems()
    );
  }

  private setMenuItems(): void {
    const { role: currentRole } = this.credentialsService.getCredentials();

    const { userPermissions } = this.permissionService;

    this.roleItems = this.getRoleSubMenuItems(userPermissions, currentRole);

    this.tenantLanguageService
      .getList()
      .pipe(
        tap((languages: TenantLanguage[]) => {
          this.supportedLanguages = languages;
          this.buildMenuItems();
        }),
        takeUntil(this.compUnsubscribe$)
      )
      .subscribe();
  }

  private buildMenuItems(): void {
    this.items = [
      {
        label: $localize`menuItems.editProfile`,
        icon: PrimeIcons.COG,
        command: () => this.goToProfile(),
      },
      {
        label: $localize`menuItems.languages`,
        icon: PrimeIcons.GLOBE,
        items: this.supportedLanguages.map((lang: TenantLanguage) => ({
          label: LANGUAGE_LABELS[lang.languageCode] || lang.languageCode,
          styleClass:
            this.languageService.getLocale() === lang.languageCode
              ? 'highlight'
              : '',
          command: () => this.changeLanguage(lang.languageCode),
        })),
      },
      {
        label: $localize`menuItems.logout`,
        icon: PrimeIcons.SIGN_OUT,
        command: () => this.logout(),
      },
    ];
  }

  private goToProfile(): void {
    this.router.navigate([AppRootRoute.PROFILE, MyProfileRoute]);
  }

  private getRoleSubMenuItems(
    userPermissions: UserPermission[],
    currentRole: TenantRole
  ): MenuItem[] {
    return userPermissions.map(({ role }) => ({
      label: role.name,
      command: () => currentRole.id !== role.id && this.switchRole(role),
      styleClass: currentRole.id === role.id ? 'highlight' : '',
    }));
  }

  private changeLanguage(lang: string): void {
    this.personalProfileService
      .getUserProfile(this.credentialsService.getCredentials().externalId)
      .pipe(
        switchMap(result => {
          this.personalProfileService.personalInfo = result;
          const param = { ...result, preferredLanguage: lang };

          return this.personalProfileService.update(param).pipe(
            tap(() => {
              this.personalProfileService.personalInfo = param;
              this.personalProfileService.hasMadeChange$.next(false);
            })
          );
        }),
        takeUntil(this.compUnsubscribe$)
      )
      .subscribe((profile: ProfileDetail) => {
        this.languageService.switchLanguage(profile.preferredLanguage);
      });
  }
}
